import { CFSEvent, ICFSLog, IUser } from '@/models';

export type TimelineLogItem = ICFSLog & {
  overriddenAt?: number | null;
  groupDay?: number | null;
  sortOrder?: number;
  editedBy?: IUser | null;
  editedAt?: number | null;
  isSynthetic?: boolean;
  isNew?: boolean;
  isDeleted?: boolean;
};

export const getEffectiveTimestamp = (log: TimelineLogItem): number | null => {
  if (log.overriddenAt === null) return null;
  if (typeof log.overriddenAt === 'number') return log.overriddenAt;
  return new Date(log.createdAt).getTime();
};

export const orderLogsForDisplay = (
  items: TimelineLogItem[],
): TimelineLogItem[] => {
  return items
    .map((item, index) => ({
      item,
      index,
      sortOrder: typeof item.sortOrder === 'number' ? item.sortOrder : null,
    }))
    .sort((a, b) => {
      if (a.sortOrder !== null && b.sortOrder !== null)
        return a.sortOrder - b.sortOrder;
      if (a.sortOrder !== null) return -1;
      if (b.sortOrder !== null) return 1;

      const aTime = getEffectiveTimestamp(a.item);
      const bTime = getEffectiveTimestamp(b.item);

      if (aTime !== null && bTime !== null) {
        if (aTime !== bTime) return aTime - bTime;
      } else if (aTime === null && bTime !== null) {
        return 1;
      } else if (aTime !== null && bTime === null) {
        return -1;
      }

      const aCreated = new Date(a.item.createdAt).getTime();
      const bCreated = new Date(b.item.createdAt).getTime();
      if (aCreated !== bCreated) return aCreated - bCreated;
      return a.index - b.index;
    })
    .map(({ item }) => item);
};

export const addMissingSyntheticEvents = (
  logs: ICFSLog[],
  cfsId: string,
): TimelineLogItem[] => {
  const normalizedLogs: TimelineLogItem[] = logs.map((log) => ({ ...log }));
  const existingEvents = new Set(normalizedLogs.map((log) => log.event));

  const fallbackCreatedAt =
    logs.length > 0
      ? new Date(Math.min(...logs.map((l) => new Date(l.createdAt).getTime())))
      : new Date();

  const callEvents = [CFSEvent.CALL_RECEIVED, CFSEvent.CALL_ANSWERED];
  const syntheticLogs: TimelineLogItem[] = callEvents
    .filter((event) => !existingEvents.has(event))
    .map((event) => ({
      _id: `synthetic:${event}`,
      app: 'account',
      cfs: cfsId,
      database: '',
      event,
      field: '',
      data: {},
      logCollection: '',
      collection: '',
      createdBy: {} as IUser,
      account: '',
      createdAt: fallbackCreatedAt,
      updatedAt: fallbackCreatedAt,
      overriddenAt: null,
      groupDay: null,
      sortOrder: undefined,
      isSynthetic: true,
    }));

  return [...normalizedLogs, ...syntheticLogs];
};
